import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ChartFactory from "src/charts/ChartFactory";
import { getChartEntry } from "src/charts/registry";
import { exportDashboardWidgetDataApi } from "src/api/dashboardApi";
import { downloadExportFile, type ReportExportResponse } from "src/helpers/Report";
import WidgetPlaceholder from "./WidgetPlaceholder";
import type { WidgetDownloadOptions } from "./DownloadOptionsMenu";

type BackendDownloadKind = "csv" | "case_data";

type ChartWidgetProps = {
  isLoading: boolean;
  title: string;
  description?: string | null;
  data: unknown;
  spec?: Record<string, unknown>;
  chartType: string;
  widgetId?: string | number;
  dashboardName?: string;
  appliedFilters?: Record<string, unknown>;
  // Backend-declared download capability — merged into ChartFactory's
  // existing native Highcharts menu (Convert to X, table view, etc.) rather
  // than replacing it, since bar/line_with_bar already have real menu items
  // there worth keeping.
  hasDownloadOptions?: boolean;
  downloadOptions?: WidgetDownloadOptions;
};

export default function ChartWidget({
  isLoading,
  title,
  description,
  data,
  spec,
  chartType,
  widgetId,
  dashboardName,
  appliedFilters,
  hasDownloadOptions,
  downloadOptions,
}: ChartWidgetProps) {
  const { mutate: downloadBackendData } = useMutation<
    ReportExportResponse,
    Error,
    BackendDownloadKind
  >({
    mutationFn: (kind) => {
      if (!dashboardName || widgetId === undefined || widgetId === null) {
        throw new Error("Missing widget context for download.");
      }
      // Confirmed backend contract: same endpoint for both, distinguished
      // by fields INSIDE `filters` — "csv" forces top_n:"all" (a download
      // shouldn't be capped at whatever's on screen), "case_data" asks for
      // one row per case via download_level:"detail" and drops top_n.
      const filters =
        kind === "case_data"
          ? { ...(appliedFilters ?? {}), download_level: "detail" }
          : { ...(appliedFilters ?? {}), top_n: "all" };
      return exportDashboardWidgetDataApi(
        dashboardName,
        String(widgetId),
        { filters },
        "xlsx",
      );
    },
    onSuccess: async (response, kind) => {
      const normalizedTitle =
        (title || "chart")
          .trim()
          .replace(/[\\/:*?"<>|]+/g, "_")
          .replace(/\s+/g, "_") || "chart";
      const suffix = kind === "case_data" ? "-case-data" : "";
      try {
        await downloadExportFile(
          {
            ...response,
            filename: `${normalizedTitle}${suffix}.${response.format || "xlsx"}`,
          },
          // The user's own filters, not the augmented `filters` posted to the
          // endpoint — top_n/download_level there are request mechanics.
          { appliedFilters },
        );
      } catch {
        toast.error("Failed to process download file.");
      }
    },
    onError: (error) => {
      toast.error(error.message || "Failed to download widget data.");
    },
  });

  if (isLoading || data === undefined || data === null) {
    return <WidgetPlaceholder height={320} />;
  }

  const entry = getChartEntry(chartType);
  const fillHeight = entry?.fillHeight ?? false;

  return (
    <ChartFactory
      chartType={chartType}
      chartId={`${widgetId ?? `${title?.replace(/\s+/g, "-") || "chart"}-${chartType}`}`}
      title={title}
      description={description}
      data={data}
      spec={spec}
      fillHeight={fillHeight}
      downloadOptions={hasDownloadOptions ? downloadOptions : undefined}
      onDownloadCsvData={() => downloadBackendData("csv")}
      onDownloadCaseData={() => downloadBackendData("case_data")}
    />
  );
}
